/* eslint global-require: 0, import/newline-after-import: 0 */

import 'location-origin'
import 'focus-visible'
require('smoothscroll-polyfill').polyfill()

import React from 'react'
import ReactDOM from 'react-dom'
import moment from 'moment'
import { getCookieValue } from 'helpers'
import { cleanQueryParams, constants, sentry } from 'common/helpers'
import { getAllQueueProductsArray } from 'common/helpers/getters'
import context from 'common/helpers/analytics/snowplow/context'
import { router } from 'sb-react-router'
import { preloadReady } from 'react-loadable'

import 'scss/fonts/index.scss'


cleanQueryParams()
moment.locale('en-us')


const getAnalyticsTracker = ({ store, fbq, dataLayer }) => {
  let prevPathname

  return (pathname) => {
    if (prevPathname === pathname) {
      return
    }

    const {
      models: {
        me: {
          id,
          email,
          gender,
          isLoggedIn,
          lifetimeStatus,
          fbPixelProperties,
          marketing: {
            referrerLandingPage,
            firstTouch: { medium, campaign, source },
          },
        },
        subscription: { isSubscribed, isCancelled, isNotSubscribed } = {},
        cart: { items: cartItems = [], totalPrice, totalCount },
      },
    } = store.getState()

    const isMakeupIntroFlowEnabled  = Boolean(getCookieValue(constants.cookieNames.makeupIntroFlow))
    const isSkincareIntroFlow       = Boolean(getCookieValue(constants.cookieNames.skincareIntroFlow))

    if (isMakeupIntroFlowEnabled || isSkincareIntroFlow) {
      fbq.trackWithPixel('PageView', constants.facebookPixels.standard)
    }

    fbq.track('PageView')

    let subscriptionStatus

    if (isSubscribed) {
      subscriptionStatus = 'subscribed'
    }
    else if (!isLoggedIn || isNotSubscribed) {
      subscriptionStatus = 'notSubscribed'
    }
    else if (isCancelled) {
      subscriptionStatus = 'cancelled'
    }

    dataLayer.track('pageView', {
      user: {
        id,
        email,
        gender,
        isLoggedIn,
        lifetimeStatus,
        subscriptionStatus,
        referrerLandingPage,
        utmMedium: medium,
        utmSource: source,
        utmCampaign: campaign,
      },
      count: totalCount,
      revenue: totalPrice / 100,
      cart: JSON.stringify(cartItems),
      fbPixelProperties,
    })

    if (pathname) {
      prevPathname = pathname
    }
  }
}

const createRouteSwitchHandler = ({ dataLayer }) => {
  let currentMatch

  return (match) => {
    if (!currentMatch || currentMatch.path !== match.path) {
      const { page: pageName } = context.setFromRouteMatch(match)

      if (pageName) {
        dataLayer.push({ pageName })
      }
    }

    currentMatch = match
  }
}

const handleRouteChange = ({ store, pathname }) => {
  const {
    models: {
      me: { isLoggedIn, id, lifetimeStatus = null },
      subscription: { status = null, subscriptionDate = null } = {},
      queue: { items: queueMonths },
    },
  } = store.getState()

  const allQueueProducts  = getAllQueueProductsArray(queueMonths, true)
  const queueFullness     = allQueueProducts.length

  if (typeof window.trackSnowplowPageView === 'function') {
    window.trackSnowplowPageView(pathname, {
      isLoggedIn,
      userId: id,
      subscriptionStatus: status,
      subscriptionDateTime: subscriptionDate,
      lifetimeStatus,
      queueFullness,
    })
  }
  else {
    sentry.captureException('window.trackSnowplowPageView not defined')
  }
}

const render = async (store, storeEvents, translationMessages, isDEV) => {
  const RootProvider      = require('containers/RootProvider/RootProvider').default
  const analytics         = require('common/helpers/analytics')

  // --------------------------------------------------------- /

  const history                 = window.__history__
  const renderAction            = isDEV ? ReactDOM.render : ReactDOM.hydrate
  const tryTrackAnalytics       = getAnalyticsTracker({ store, ...analytics })
  const handleRouteSwitch       = createRouteSwitchHandler(analytics)
  const cosmeticsDataStructure  = store.getState().view.cosmeticsPage.structure

  // --------------------------------------------------------- /

  tryTrackAnalytics(window.location.pathname)

  router.subscribe(handleRouteSwitch)

  history.listen(({ pathname }) => {
    console.log('route changed to:', pathname)

    handleRouteChange({ store, pathname })
    tryTrackAnalytics(pathname)
  })

  if (!isDEV) {
    await preloadReady()
  }

  renderAction(
    <RootProvider {...{ store, storeEvents, history, translationMessages, cosmeticsDataStructure }} />,
    document.getElementById('root')
  )
}

const init = (store, storeEvents, translationMessages, isDEV) => {
  // https://developers.google.com/web/updates/2016/04/intersectionobserver
  const isIntersectionObserverExist = (
    'IntersectionObserver' in window
    && 'IntersectionObserverEntry' in window
    && 'intersectionRatio' in window.IntersectionObserverEntry.prototype
  )

  if (isIntersectionObserverExist) {
    // Minimal polyfill for Edge 15's lack of `isIntersecting`
    // See: https://github.com/w3c/IntersectionObserver/issues/211
    if (!('isIntersecting' in window.IntersectionObserverEntry.prototype)) {
      Object.defineProperty(
        window.IntersectionObserverEntry.prototype,
        'isIntersecting',
        {
          get() {
            return this.intersectionRatio > 0
          },
        }
      )
    }

    render(store, storeEvents, translationMessages, isDEV)
  }
  else {
    require.ensure([ 'sb-intersection-observer/polyfill' ], (require) => {
      require('sb-intersection-observer/polyfill')
      render(store, storeEvents, translationMessages, isDEV)
    })
  }
}


export default init
